import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";

// Transform frontend form data to API format
const transformFormDataToAPI = (formData) => {
  return {
    patient_id: formData.patient_id || formData.patientId,
    patient_identification_number:
      formData.patient_identification_number ||
      formData.patientIdentificationNumber,
    patient_name: formData.patient_name || formData.patientName,
    patient_dob: formData.patient_dob || formData.patientDob,
    patient_address: formData.patient_address || formData.patientAddress,
    patient_phone: formData.patient_phone || formData.patientPhone,
    patient_email: formData.patient_email || formData.patientEmail,
    referring_doctor: formData.referring_doctor || formData.referringDoctor,
    requested_tests: formData.requested_tests || formData.requestedTests || [],
    other_tests: formData.other_tests || formData.otherTests,
    clinical_notes: formData.clinical_notes || formData.clinicalNotes,
    preferred_date: formData.preferred_date || formData.preferredDate,
    preferred_time_slot:
      formData.preferred_time_slot || formData.preferredTimeSlot,
    assigned_nurse: formData.assigned_nurse || formData.assignedNurse,
    scheduled_visit_datetime:
      formData.scheduled_visit_datetime || formData.scheduledVisitDatetime,
    lab_partner: formData.lab_partner || formData.labPartner,
    results_received: formData.results_received || formData.resultsReceived,
    results_shared_with_patient:
      formData.results_shared_with_patient || formData.resultsSharedWithPatient,
    results_shared_with_doctor:
      formData.results_shared_with_doctor || formData.resultsSharedWithDoctor,
    follow_up_required:
      formData.follow_up_required || formData.followUpRequired,
    follow_up_notes: formData.follow_up_notes || formData.followUpNotes,
  };
};

// Transform API response to frontend format
const transformAPIToFormData = (apiData) => {
  return {
    id: apiData.id,
    patient_id: apiData.patientId,
    patient_name: apiData.patientName,
    patient_dob: apiData.patientDob,
    patient_address: apiData.patientAddress,
    patient_phone: apiData.patientPhone,
    patient_email: apiData.patientEmail,
    referring_doctor: apiData.referringDoctor,
    requested_tests: apiData.requestedTests || [],
    other_tests: apiData.otherTests,
    clinical_notes: apiData.clinicalNotes,
    preferred_date: apiData.preferredDate,
    preferred_time_slot: apiData.preferredTimeSlot,
    status: apiData.status,
    assigned_nurse: apiData.assignedNurse,
    scheduled_visit_datetime: apiData.scheduledVisitDatetime,
    lab_partner: apiData.labPartner,
    results_received: apiData.resultsReceived,
    results_shared_with_patient: apiData.resultsSharedWithPatient,
    results_shared_with_doctor: apiData.resultsSharedWithDoctor,
    follow_up_required: apiData.followUpRequired,
    follow_up_notes: apiData.followUpNotes,
    created_date: apiData.createdAt,
    updated_date: apiData.updatedAt,
    created_by: apiData.createdBy,
    updated_by: apiData.updatedBy,
  };
};

// Query Keys
export const conciergeTestKeys = {
  all: ["conciergeTests"],
  list: (filters) => ["conciergeTests", "list", filters],
  detail: (id) => ["conciergeTests", "detail", id],
  byStatus: (status) => ["conciergeTests", "status", status],
  byPractice: () => ["conciergeTests", "practice"],
  byUser: () => ["conciergeTests", "user"],
  config: () => ["conciergeTests", "config"],
};

// Get Configuration Hook
export const useConciergeTestConfig = () => {
  return useQuery({
    queryKey: conciergeTestKeys.config(),
    queryFn: async () => {
      return await apiClient.get("/api/concierge-testing/config");
    },
    staleTime: 30 * 60 * 1000, // 30 minutes - config rarely changes
  });
};

// Get All Requests Hook
export const useConciergeTestRequests = (filters = {}) => {
  return useQuery({
    queryKey: conciergeTestKeys.list(filters),
    queryFn: async () => {
      const response = await apiClient.get("/api/concierge-testing", filters);
      return response.map(transformAPIToFormData);
    },
    staleTime: 30 * 1000, // 30 seconds
  });
};

// Get Request by ID Hook
export const useConciergeTestRequest = (requestId) => {
  return useQuery({
    queryKey: conciergeTestKeys.detail(requestId),
    queryFn: async () => {
      const response = await apiClient.get(
        `/api/concierge-testing/${requestId}`
      );
      return transformAPIToFormData(response);
    },
    enabled: !!requestId,
    staleTime: 30 * 1000,
  });
};

// Get Requests by Status Hook
export const useConciergeTestRequestsByStatus = (status, practiceId = null) => {
  return useQuery({
    queryKey: conciergeTestKeys.byStatus(status),
    queryFn: async () => {
      const params = practiceId ? { practiceId } : {};
      const response = await apiClient.get(
        `/api/concierge-testing/status/${status}`,
        params
      );
      return response.map(transformAPIToFormData);
    },
    enabled: !!status,
    staleTime: 30 * 1000,
  });
};

// Get Practice Requests Hook
export const usePracticeConciergeTestRequests = (limit = null) => {
  return useQuery({
    queryKey: conciergeTestKeys.byPractice(),
    queryFn: async () => {
      const params = limit ? { limit } : {};
      const response = await apiClient.get(
        "/api/concierge-testing/practice/current",
        params
      );
      return response.map(transformAPIToFormData);
    },
    staleTime: 30 * 1000,
  });
};

// Get User Requests Hook
export const useUserConciergeTestRequests = (limit = null) => {
  return useQuery({
    queryKey: conciergeTestKeys.byUser(),
    queryFn: async () => {
      const params = limit ? { limit } : {};
      const response = await apiClient.get(
        "/api/concierge-testing/user/current",
        params
      );
      return response.map(transformAPIToFormData);
    },
    staleTime: 30 * 1000,
  });
};

// Create Request Mutation
export const useCreateConciergeTestRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestData) => {
      const transformedData = transformFormDataToAPI(requestData);
      const response = await apiClient.post(
        "/api/concierge-testing",
        transformedData
      );
      return transformAPIToFormData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conciergeTestKeys.all });
    },
  });
};

// Update Request Mutation
export const useUpdateConciergeTestRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, requestData }) => {
      const transformedData = transformFormDataToAPI(requestData);
      const response = await apiClient.put(
        `/api/concierge-testing/${requestId}`,
        transformedData
      );
      return transformAPIToFormData(response);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: conciergeTestKeys.all });
      queryClient.invalidateQueries({
        queryKey: conciergeTestKeys.detail(variables.requestId),
      });
    },
  });
};

// Update Request Status Mutation
export const useUpdateConciergeTestRequestStatus = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, status, additionalData = {} }) => {
      return await apiClient.patch(
        `/api/concierge-testing/${requestId}/status`,
        {
          status,
          ...additionalData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conciergeTestKeys.all });
    },
  });
};

// Delete Request Mutation
export const useDeleteConciergeTestRequest = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId) => {
      return await apiClient.delete(`/api/concierge-testing/${requestId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: conciergeTestKeys.all });
    },
  });
};

// Export transformer functions for backward compatibility
export { transformFormDataToAPI, transformAPIToFormData };
